import { knowledgeModuleNavActive } from "@/lib/knowledgePaths";
import {
  BookMarked,
  BookOpen,
  CalendarClock,
  CreditCard,
  Eye,
  FileText,
  FolderOpen,
  Bot,
  Gauge,
  Home,
  LayoutDashboard,
  Library,
  Link2,
  Mail,
  ShoppingCart,
  Users,
  type LucideIcon,
} from "lucide-react";

export type AgentsModule = {
  name: string;
  href: string;
  icon: LucideIcon;
};

export type AgentsAppSlug =
  | "home"
  | "agents"
  | "knowledge"
  | "wiki"
  | "overseer"
  | "connections"
  | "schedules"
  | "credits"
  | "users"
  | "billing";

export type AgentsApp = {
  slug: AgentsAppSlug;
  label: string;
  topNavHref: string;
  pathPrefixes: string[];
  modules: AgentsModule[];
};

export const AGENTS_APPS: AgentsApp[] = [
  {
    slug: "home",
    label: "Workforce",
    topNavHref: "/",
    pathPrefixes: ["/", "/settings"],
    modules: [{ name: "Dashboard", href: "/", icon: LayoutDashboard }],
  },
  {
    slug: "agents",
    label: "Agents",
    topNavHref: "/agents",
    pathPrefixes: ["/agents", "/seats"],
    modules: [{ name: "Agents", href: "/agents", icon: Bot }],
  },
  {
    slug: "knowledge",
    label: "Knowledge",
    topNavHref: "/knowledge",
    pathPrefixes: ["/knowledge"],
    modules: [
      { name: "Knowledge base", href: "/knowledge", icon: BookOpen },
      { name: "Libraries", href: "/knowledge/libraries", icon: FolderOpen },
      { name: "Books", href: "/knowledge/books", icon: Library },
      { name: "Articles", href: "/knowledge/articles", icon: FileText },
    ],
  },
  {
    slug: "overseer",
    label: "Overseer",
    topNavHref: "/overseer",
    pathPrefixes: ["/overseer"],
    modules: [{ name: "Overseer", href: "/overseer", icon: Eye }],
  },
  {
    slug: "connections",
    label: "Connections",
    topNavHref: "/connections",
    pathPrefixes: ["/connections"],
    modules: [
      { name: "Connections", href: "/connections", icon: Link2 },
      { name: "Email", href: "/connections/email", icon: Mail },
    ],
  },
  {
    slug: "schedules",
    label: "Schedules",
    topNavHref: "/schedules",
    pathPrefixes: ["/schedules"],
    modules: [{ name: "Schedules", href: "/schedules", icon: CalendarClock }],
  },
  {
    slug: "credits",
    label: "Credits",
    topNavHref: "/credits",
    pathPrefixes: ["/credits"],
    modules: [{ name: "Credits", href: "/credits", icon: Gauge }],
  },
  {
    slug: "users",
    label: "Users",
    topNavHref: "/users",
    pathPrefixes: ["/users"],
    modules: [{ name: "Users", href: "/users", icon: Users }],
  },
  {
    slug: "billing",
    label: "Billing",
    topNavHref: "/billing",
    pathPrefixes: ["/billing", "/checkout"],
    modules: [
      { name: "Billing", href: "/billing", icon: CreditCard },
      { name: "Checkout", href: "/checkout", icon: ShoppingCart },
    ],
  },
  {
    slug: "wiki",
    label: "Wiki",
    topNavHref: "/wiki",
    pathPrefixes: ["/wiki"],
    modules: [{ name: "Wiki", href: "/wiki", icon: BookMarked }],
  },
];

/** Top bar order: Workforce, Agents, and Knowledge first, then the rest. */
export const TOP_NAV_APP_ORDER: AgentsAppSlug[] = [
  "home",
  "agents",
  "knowledge",
  "overseer",
  "connections",
  "schedules",
  "credits",
  "users",
  "billing",
  "wiki",
];

export const AGENTS_APP_BY_SLUG: Record<AgentsAppSlug, AgentsApp> = Object.fromEntries(
  AGENTS_APPS.map((app) => [app.slug, app]),
) as Record<AgentsAppSlug, AgentsApp>;

function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  if (prefix === "/") {
    return pathname === "/" || pathname === "" || pathname.startsWith("/settings");
  }
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function resolveAgentsApp(pathname: string): AgentsApp {
  for (const slug of TOP_NAV_APP_ORDER) {
    if (slug === "home") continue;
    const app = AGENTS_APP_BY_SLUG[slug];
    if (app.pathPrefixes.some((prefix) => pathMatchesPrefix(pathname, prefix))) {
      return app;
    }
  }
  return AGENTS_APP_BY_SLUG.home;
}

export function topNavAppActive(pathname: string, slug: AgentsAppSlug): boolean {
  return resolveAgentsApp(pathname).slug === slug;
}

const KNOWLEDGE_SIDEBAR_HREFS = new Set([
  "/knowledge",
  "/knowledge/libraries",
  "/knowledge/books",
  "/knowledge/articles",
]);

export function moduleNavActive(pathname: string, href: string): boolean {
  if (href === "/") {
    return pathname === "/" || pathname === "";
  }
  if (href === "/agents") {
    return pathname === "/agents" || pathname.startsWith("/seats/");
  }
  if (KNOWLEDGE_SIDEBAR_HREFS.has(href)) {
    return knowledgeModuleNavActive(pathname, href);
  }
  if (href === "/connections") {
    return (
      pathname === "/connections" ||
      pathname === "/connections/new" ||
      (pathname.startsWith("/connections/") && !pathname.startsWith("/connections/email"))
    );
  }
  if (href === "/connections/email") {
    return pathname === "/connections/email" || pathname.startsWith("/connections/email/");
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function appIcon(slug: AgentsAppSlug): LucideIcon {
  switch (slug) {
    case "home":
      return Home;
    case "agents":
      return Bot;
    case "knowledge":
      return Library;
    case "wiki":
      return BookMarked;
    case "overseer":
      return Eye;
    case "connections":
      return Link2;
    case "schedules":
      return CalendarClock;
    case "credits":
      return Gauge;
    case "users":
      return Users;
    case "billing":
      return CreditCard;
  }
}
